/**
 * Centralized permission utilities for role-based access control.
 * 
 * Access levels (for viewing review/response TEXT content):
 * - HR (isHR flag): Full text access to all reviews and responses
 * - Managers: Full text access for their direct reports
 * - Users: Can see their own data unredacted
 * - Everyone else (Super Admins, Admins): Can see reviews/responses exist but text is REDACTED
 * 
 * Note: Super Admins still have full system admin access (manage users, templates, etc.)
 * but do NOT bypass text redaction for privacy reasons.
 */

export const SUPER_ADMIN_EMAILS = [
  'hunter@stitchdesignco.com',
  'laine@stitchdesignco.com'
];

export interface UserProfile {
  role: string;
  isHR: boolean;
  isSuperHR: boolean;
  managers: Array<{ id: string; name: string; email: string }>;
  email: string;
  displayName: string;
}

/** Check if email belongs to a super admin */
export const isSuperAdmin = (email?: string | null): boolean => {
  if (!email) return false;
  return SUPER_ADMIN_EMAILS.includes(email.toLowerCase());
};

/** Check if user has admin-level access (can access admin pages) */
export const hasAdminAccess = (
  email?: string | null,
  userProfile?: UserProfile | null
): boolean => {
  if (isSuperAdmin(email)) return true;
  if (userProfile?.role === 'admin') return true;
  if (userProfile?.isHR) return true;
  if (userProfile?.isSuperHR) return true;
  return false;
};

/** Check if user is a manager (has direct reports assigned to them) */
export const isManager = (userProfile?: UserProfile | null): boolean => {
  return userProfile?.role === 'manager';
};

/**
 * Check if user can manage team members (toggle peer review / pulse check).
 * Admins manage everyone; managers manage their own direct reports.
 */
export const hasManagementAccess = (
  email?: string | null,
  userProfile?: UserProfile | null
): boolean => {
  return hasAdminAccess(email, userProfile) || isManager(userProfile);
};

/** Check if the viewer is a manager of the target user (target's managers include viewer) */
export const isManagerOf = (
  viewerUid: string,
  targetManagers?: Array<{ id: string }> | null
): boolean => {
  return Boolean(targetManagers && targetManagers.some(m => m.id === viewerUid));
};

/** Check if user has HR access (either HR or Super HR) */
export const isHR = (userProfile?: UserProfile | null): boolean => {
  return userProfile?.isHR === true || userProfile?.isSuperHR === true;
};

/** Check if user is Super HR (the dedicated HR person) */
export const isSuperHRUser = (userProfile?: UserProfile | null): boolean => {
  return userProfile?.isSuperHR === true;
};

/** 
 * Check if the viewer can see full (unredacted) text for a target user's reviews/responses.
 * 
 * Full text is visible when:
 * 1. Viewer has HR flag (HR can see everything)
 * 2. Viewer is the target user (own data)
 * 3. Viewer is a manager of the target user
 * 
 * Otherwise, text should be [REDACTED] — including super admins and regular admins
 * who don't have HR access or a manager relationship with the target user.
 */
export const canViewFullText = (
  viewerUid: string,
  viewerEmail: string | null | undefined,
  viewerProfile: UserProfile | null | undefined,
  targetUserId: string,
  targetManagers?: Array<{ id: string }> | null
): boolean => {
  // HR and Super HR users see full text
  if (viewerProfile?.isHR || viewerProfile?.isSuperHR) return true;
  
  // Users can see their own data
  if (viewerUid === targetUserId) return true;
  
  // Managers can see their direct reports' data
  if (targetManagers && targetManagers.some(m => m.id === viewerUid)) return true;
  
  // Everyone else (super admins, admins without HR/manager relationship) sees redacted
  return false;
};

/** Redaction placeholder text */
export const REDACTED_TEXT = '[REDACTED]';
export const REDACTED_EXPLANATION = 'Content is redacted. Only HR personnel and direct managers can view the full text of reviews and responses.';
